/**
 * Role Guard
 *
 * Functional route guard that restricts access to routes based on user roles.
 * Checks if the current user has the required role specified in the route data.
 * If the user doesn't have the required role, they are redirected to the dashboard.
 *
 * This guard follows the Angular 15+ functional guard pattern.
 *
 * @example
 * ```typescript
 * // In app.routes.ts
 * export const routes: Routes = [
 *   {
 *     path: 'admin-management',
 *     canActivate: [authGuard, roleGuard],
 *     data: { role: 'comp_superadmin' },
 *     component: AdminManagementComponent
 *   }
 * ];
 * ```
 */

import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { CanActivateFn } from '@angular/router';
import { UserService } from '../services/user.service';

/**
 * Guard function that checks if the user has the required role
 *
 * Reads the required role from route.data['role'] and compares it
 * with the current user's role.
 *
 * If the user has the required role, allows navigation.
 * If not, redirects to the dashboard.
 *
 * @param route - The activated route snapshot containing role data
 * @returns True if navigation is allowed, UrlTree for redirect if not
 *
 * @example
 * Route configuration (single role):
 * ```typescript
 * {
 *   path: 'admin-management',
 *   canActivate: [roleGuard],
 *   data: { role: 'comp_superadmin' }
 * }
 * ```
 *
 * Route configuration (multiple roles):
 * ```typescript
 * {
 *   path: 'branch-management',
 *   canActivate: [roleGuard],
 *   data: { role: ['comp_superadmin', 'comp_admin'] }
 * }
 * ```
 *
 * Behavior:
 * - User with matching role: Access granted
 * - User without matching role: Redirected to /dashboard
 * - No user: Redirected to /dashboard
 */
export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: import('@angular/router').RouterStateSnapshot
): boolean | UrlTree => {
  const userService = inject(UserService);
  const router = inject(Router);

  // Get the required role(s) from route data
  const requiredRole = route.data['role'] as string | string[];

  // Get the current user
  const user = userService.currentUser;

  // If no user or no required role specified, deny access
  if (!user || !requiredRole) {
    return router.createUrlTree(['/dashboard']);
  }

  // Check if user has one of the required roles
  const hasRole = Array.isArray(requiredRole)
    ? requiredRole.includes(user.role.name)
    : user.role.name === requiredRole;

  if (hasRole) {
    return true;
  }

  // User doesn't have the required role - redirect to dashboard
  return router.createUrlTree(['/dashboard']);
};
